import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../app/hooks";
import Breadcrumbs from "../components/Breadcrumbs";
import LeftMenu from "../components/LeftMenu";
import AdminReportRow from "../components/admin/AdminReportRow";
import AdminTabs from "../components/admin/AdminTabs";
import { AdminReportResponse, AdminReportStatus } from "../features/admin/contracts/AdminReportResponse";
import { PaginatedAdminReportResult } from "../features/admin/contracts/PaginatedAdminReportResult";
import { fetchAdminReports } from "../features/admin/slice-extensions/admin-moderation-slice-extension";

const PAGE_SIZE = 20;

/** The platform's moderation queue (US-111, PD-0054): reports newest first, handled within 24 hours. */
const AdminReportsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<AdminReportStatus | null>("Open");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<PaginatedAdminReportResult | null>(null);
  const [failed, setFailed] = useState(false);
  const [badgeVersion, setBadgeVersion] = useState(0);

  const load = useCallback(async () => {
    const action = await dispatch(fetchAdminReports({ status, page, pageSize: PAGE_SIZE }));
    setFailed(fetchAdminReports.rejected.match(action));
    if (fetchAdminReports.fulfilled.match(action)) setResult(action.payload);
  }, [dispatch, status, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const handled = (report: AdminReportResponse) => {
    setResult((current) =>
      current && { ...current, items: current.items.map((item) => (item.id === report.id ? report : item)) },
    );
    setBadgeVersion((v) => v + 1);
  };

  const filters: { value: AdminReportStatus | null; label: string }[] = [
    { value: "Open", label: t("admin.reports.filterOpen") },
    { value: null, label: t("admin.reports.filterAll") },
  ];
  const items = result?.items ?? [];

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <div>
                <h1>{t("admin.reports.title")}</h1>
                <p className="text-grey-40 dark:text-grey-70">{t("admin.reports.subtitle")}</p>
              </div>
              <AdminTabs reportCountVersion={badgeVersion} />
              <div className="flex gap-2" role="group" aria-label={t("admin.reports.title")}>
                {filters.map((filter) => (
                  <button
                    key={filter.label}
                    type="button"
                    className={`btn ${status === filter.value ? "btn-primary" : "btn-secondary"}`}
                    onClick={() => {
                      setStatus(filter.value);
                      setPage(1);
                    }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              {failed && (
                <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <p>{t("admin.reports.errorLoading")}</p>
                </div>
              )}
              {!result && !failed ? (
                <p className="py-10 text-center text-grey-40 dark:text-grey-70">{t("admin.loading")}</p>
              ) : items.length === 0 && !failed ? (
                <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">
                  {t("admin.reports.empty")}
                </div>
              ) : (
                <div className="space-y-2 lg:space-y-4">
                  {items.map((report) => (
                    <AdminReportRow key={report.id} report={report} onHandled={handled} />
                  ))}
                </div>
              )}
              {result && result.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    {t("admin.previous")}
                  </button>
                  <span className="text-sm text-grey-50 dark:text-grey-60">
                    {t("admin.pageOf", { page, totalPages: result.totalPages })}
                  </span>
                  <button className="btn btn-secondary" disabled={!result.hasNextPage} onClick={() => setPage((p) => p + 1)}>
                    {t("admin.next")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminReportsPage;
